import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import ListViewType from '@components/ListViewType';
import { toastError } from '@components/Toast';

import Chip from '@features/filter/Chip';
import ChipForDescriptionSearch from '@features/materials/components/ChipForDescriptionSearch';
import FabricSearchWithQR from '@features/materials/components/FabricSearchWithQR';

import { setFilters } from '@store/common/action';
import { initialFilters } from '@store/common/reducer';
import { getCommonFilters } from '@store/reducers';

import ApiClient from '@lib/apiClient';

import { VIEW_TYPE_OBJ } from '@utils/MaterialsUtil';
import { capitalizeFirstLetter, checkUserType, getCountryList } from '@utils/Util';

import { MATERIALS_TYPE } from '@/constants/constant';

import { generateFilterFabricParams } from '@/features/filter/static/filterUtil';

import CommonImage from './CommonImage';
import InlineUpload from './InlineUpload';

const TopFilter = ({
	onAdd,
	type,
	onCollspseFilter,
	title,
	totalElements,
	onSearch,
	onSortSupplier,
	activeStep,
	onSorting,
	materialStockInfo = {},
	requestFrom,
	setDefaultViewType,
	selectedCountry,
	setSelectedCountry,
	activeTab,
	selectedBalance,
	setSelectedBalance,
	selectedSort,
	setSelectedSort,
	setPage,
	setToggleOptions,
	toggleOptions,
	setIsEdit,
	setSelectedItem,
	onSetLikeAndUnlike,
	setIsInventoryModal,
	setIsIssueTab,
	handleBulkUpload
}) => {
	const [isSearchBoxHidden, setIsSearchBoxHidden] = useState(true);
	const [isSearchDone, setIsSearchDone] = useState(false);
	const { descriptionSearchText } = useSelector((store) => store?.supplier?.fabricSearch);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const params = generateFilterFabricParams(filterfields);
	const inputRef = useRef(null);

	const getSelectedBalance = () => {
		if (params.search || params.nitexReferenceNumber || params.supplierReferenceNumber) {
			return 'All Balance';
		} else {
			return selectedBalance?.value;
		}
	};

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, [isSearchBoxHidden]);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.value = '';
			setIsSearchDone(false);
		}
	}, [activeTab]);

	let commonFilters = useSelector(getCommonFilters);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const clearCategoryIdList = (id, listType) => {
		let listIds = commonFilters[listType];
		let newIdList = listIds.filter((item) => item.id !== id);
		dispatch(setFilters({ ...commonFilters, [listType]: newIdList }));
	};

	const clearCapabilities = (capability) => {
		dispatch(setFilters({ ...commonFilters, [capability]: false }));
	};

	const setSortingValue = (value) => {
		if (type === 'MATERIAL') {
			onSorting(value);
		}
	};

	const returnCapabilityChip = (value, paramName, label) => {
		if (value) {
			return (
				<div className="chip" key={paramName}>
					<span>{label}</span>
					<span className="chip-close cursor-pointer" onClick={() => clearCapabilities(paramName)}>
						<svg
							width={12}
							height={12}
							viewBox="0 0 12 12"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11.25 1.8075L10.1925 0.75L6 4.9425L1.8075 0.75L0.75 1.8075L4.9425 6L0.75 10.1925L1.8075 11.25L6 7.0575L10.1925 11.25L11.25 10.1925L7.0575 6L11.25 1.8075Z"
								fill="#6A7181"
							/>
						</svg>
					</span>
				</div>
			);
		}
	};

	const renderBulkUpload = () => {
		return (
			<div className="option bulk-upload create-option-dropdown">
				<div className="dropdown">
					<button
						className="button text size32"
						type="button"
						id="menu1"
						data-toggle="dropdown"
						aria-expanded="false"
					>
						<CommonImage src={`/icons/add-round.svg`} alt="add" />
						<span className="ml-2">{`${title}`}</span>
					</button>
					<ul
						className="dropdown-menu dropdown-menu-right shadow-lg"
						role="menu"
						aria-labelledby="downloadType"
						x-placement="bottom-end"
					>
						<li>
							<span onClick={() => onAddClick()}>Add {`${title}`}</span>
						</li>
						<li>
							<InlineUpload
								label="Upload Bulk Photo (max 100MB)"
								id="bulkUpload"
								onUpload={handleBulkUpload}
							/>
						</li>
					</ul>
				</div>
			</div>
		);
	};

	const showClearAll = () => {
		const {
			categoryIdList,
			marketIdList,
			tagIdList,
			fabricTypes,
			compositions,
			embellishmentCategories,
			embellishmentSubCategories,
			trimsCategories,
			brandingCategories,
			washProcesses,
			supplierCategory,
			certificateTypeList,
			productStrengthTagList,
			additionalTagList,
			clientTagList,
			haveEtp,
			haveWtp,
			haveLab,
			haveRnd,
			haveSample,
			haveStudio,
			isComposite,
			brandingTypes,
			trimsTypes,
			fabricCompositionPartIdList,
			constructions,
			supplierType,
			brandIds,
			nominatedBrandIds,
			pricePointList,
			seasonList,
			countryIds,
			suppliers,
			materialIdList,
			fixedTagIdList,
			colorList,
			baseList,
			subCategoryIds,
			mainCategoryIds,
			materialQualityIds,
			sustainabilityIds,
			specificBrandIds
		} = commonFilters;

		let mergedList = [
			...supplierType,
			...seasonList,
			...brandIds,
			...nominatedBrandIds,
			...pricePointList,
			...countryIds,
			...categoryIdList,
			...marketIdList,
			...tagIdList,
			...fabricTypes,
			...compositions,
			...embellishmentCategories,
			...embellishmentSubCategories,
			...trimsCategories,
			...brandingCategories,
			...washProcesses,
			...supplierCategory,
			...certificateTypeList,
			...productStrengthTagList,
			...additionalTagList,
			...clientTagList,
			...brandingTypes,
			...trimsTypes,
			...fabricCompositionPartIdList,
			...constructions,
			...suppliers,
			...materialIdList,
			...colorList,
			...baseList,
			...subCategoryIds,
			...mainCategoryIds,
			...materialQualityIds,
			...sustainabilityIds,
			...specificBrandIds
		];

		if (
			mergedList.length > 0 ||
			haveEtp ||
			haveWtp ||
			haveLab ||
			haveRnd ||
			haveSample ||
			haveStudio ||
			fixedTagIdList?.length > 0 ||
			commonFilters?.designNumber ||
			commonFilters?.countryId ||
			commonFilters?.cityId ||
			isComposite
		)
			return (
				<span className="clear" onClick={() => dispatch(setFilters(initialFilters))}>
					Clear all
				</span>
			);

		return <></>;
	};

	const renderSingleChip = (filteredList = [], filterName) => {
		return filteredList?.map((item) => (
			<div className="chip" key={item.id}>
				<span>{capitalizeFirstLetter(item.name)}</span>
				<span
					className="chip-close cursor-pointer"
					onClick={() => clearCategoryIdList(item.id, filterName)}
				>
					<svg
						width={12}
						height={12}
						viewBox="0 0 12 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M11.25 1.8075L10.1925 0.75L6 4.9425L1.8075 0.75L0.75 1.8075L4.9425 6L0.75 10.1925L1.8075 11.25L6 7.0575L10.1925 11.25L11.25 10.1925L7.0575 6L11.25 1.8075Z"
							fill="#6A7181"
						/>
					</svg>
				</span>
			</div>
		));
	};

	const renderFilteredItems = () => {
		const {
			suppliers,
			materialIdList,
			categoryIdList,
			seasonList,
			marketIdList,
			tagIdList,
			fabricTypes,
			compositions,
			embellishmentCategories,
			embellishmentSubCategories,
			brandingCategories,
			washProcesses,
			supplierCategory,
			certificateTypeList,
			productStrengthTagList,
			additionalTagList,
			clientTagList,
			brandingTypes,
			trimsTypes,
			fabricCompositionPartIdList,
			fixedTagIdList,
			colorList,
			constructions,
			supplierType,
			brandIds,
			nominatedBrandIds,
			baseList,
			pricePointList,
			countryIds,
			subCategoryIds,
			mainCategoryIds,
			materialQualityIds,
			sustainabilityIds,
			specificBrandIds
		} = commonFilters;

		return (
			<>
				{returnCapabilityChip(commonFilters.haveEtp, 'haveEtp', 'ETP')}
				{returnCapabilityChip(commonFilters.haveLab, 'haveLab', 'Lab')}
				{returnCapabilityChip(commonFilters.haveRnd, 'haveRnd', 'R&D')}
				{returnCapabilityChip(commonFilters.haveSample, 'haveSample', 'Sample')}
				{returnCapabilityChip(commonFilters.haveStudio, 'haveStudio', 'Studio')}
				{returnCapabilityChip(commonFilters.haveWtp, 'haveWtp', 'WTP')}
				{returnCapabilityChip(commonFilters.isComposite, 'isComposite', 'Composite')}
				{returnCapabilityChip(commonFilters.minGsm, 'minGsm', commonFilters.minGsm)}
				{returnCapabilityChip(commonFilters.maxGsm, 'maxGsm', commonFilters.maxGsm)}
				{returnCapabilityChip(commonFilters.countryId, 'countryId', commonFilters['countryId'])}
				{returnCapabilityChip(commonFilters.cityId, 'cityId', commonFilters['cityId_value'])}
				{returnCapabilityChip(commonFilters.designNumber, 'designNumber', commonFilters.designNumber)}

				{renderSingleChip(suppliers, 'suppliers')}
				{renderSingleChip(materialIdList, 'materialIdList')}
				{renderSingleChip(constructions, 'constructions')}
				{renderSingleChip(fabricCompositionPartIdList, 'fabricCompositionPartIdList')}
				{renderSingleChip(fixedTagIdList, 'fixedTagIdList')}
				{renderSingleChip(colorList, 'colorList')}
				{renderSingleChip(brandingTypes, 'brandingTypes')}
				{renderSingleChip(trimsTypes, 'trimsTypes')}
				{renderSingleChip(categoryIdList, 'categoryIdList')}
				{renderSingleChip(marketIdList, 'marketIdList')}
				{renderSingleChip(tagIdList, 'tagIdList')}
				{renderSingleChip(fabricTypes, 'fabricTypes')}
				{renderSingleChip(compositions, 'compositions')}
				{renderSingleChip(embellishmentCategories, 'embellishmentCategories')}
				{renderSingleChip(embellishmentSubCategories, 'embellishmentSubCategories')}
				{renderSingleChip(subCategoryIds, 'subCategoryIds')}
				{renderSingleChip(mainCategoryIds, 'mainCategoryIds')}
				{renderSingleChip(materialQualityIds, 'materialQualityIds')}
				{renderSingleChip(sustainabilityIds, 'sustainabilityIds')}
				{renderSingleChip(specificBrandIds, 'specificBrandIds')}
				{renderSingleChip(brandingCategories, 'brandingCategories')}
				{renderSingleChip(washProcesses, 'washProcesses')}
				{renderSingleChip(supplierCategory, 'supplierCategory')}
				{renderSingleChip(certificateTypeList, 'certificateTypeList')}
				{renderSingleChip(productStrengthTagList, 'productStrengthTagList')}
				{renderSingleChip(additionalTagList, 'additionalTagList')}
				{renderSingleChip(clientTagList, 'clientTagList')}
				{renderSingleChip(supplierType, 'supplierType')}
				{renderSingleChip(countryIds, 'countryIds')}
				{renderSingleChip(brandIds, 'brandIds')}
				{renderSingleChip(nominatedBrandIds, 'nominatedBrandIds')}
				{renderSingleChip(baseList, 'baseList')}
				{renderSingleChip(pricePointList, 'pricePointList')}
				{renderSingleChip(seasonList, 'seasonList')}
			</>
		);
	};

	const onAddClick = () => {
		if (type === 'MATERIAL') {
			onAdd();
		}
		if (type === 'SUPPLIER') {
			ApiClient.POST('supplierAddFromListEvent', {})
				.then(() => {
					navigate('/suppliers/add');
				})
				.catch(() => {
					toastError('Event was unsuccessful!');
				});
		}
	};

	const inventoryInfo = (stock, isKnit) => {
		return (
			<li>
				<span className="regular-14 gray_dark_02 weight-500">
					{stock?.fabricType ? stock?.fabricType : ''}
					{': '}
					{stock?.sum ? parseInt(stock?.sum) : 0} {stock?.unit}
					{isKnit ? ' & ' : ', '}
					{isKnit ? stock?.pcsCount > 0 && `${Math.floor(stock?.pcsCount)} pcs, ` : null}
					{stock?.count}
					{' Items'}
				</span>
			</li>
		);
	};

	const onKeyPressUpdate = (event) => {
		if (event.key === 'Enter' && !isSearchDone) {
			setPage && setPage(0);
			window.scroll(0, 0);
			let searchInputValue = '';
			searchInputValue = event.target.value;
			onSearch(searchInputValue);
			setIsSearchDone(true);
		}
	};

	return (
		<div className="filter-container-top d-block">
			<div className="d-flex align-items-center justify-content-between">
				{requestFrom !== 'CRM' && (
					<div className="filters">
						<div className="filter-toggle no-after pl-0" onClick={onCollspseFilter}>
							<img src={`${process.env.PUBLIC_URL}/icons/filter24.svg`} alt="filter" />
							<span>Filter</span>
						</div>
						{requestFrom === undefined && totalElements && totalElements > 0 ? (
							<span className="common-list-component mt-1 mr-2">{totalElements} Items </span>
						) : (
							<></>
						)}
						{
							<ul className="common-list-component mt-1 mr-2">
								{type !== 'SUPPLIER' && activeStep !== 'GARMENTS' ? (
									<li>
										<span className="regular-14 gray_dark_02 weight-500">
											{'Total inventory: '}
											{materialStockInfo?.totalInventory}
											{materialStockInfo?.totalInventory > 1 ? ' Items' : ' Item'}
										</span>
									</li>
								) : (
									<span className="regular-14 gray_dark_02 weight-500">
										{totalElements}
										{totalElements > 1 ? ' Items' : ' Item'}
									</span>
								)}
								{requestFrom === MATERIALS_TYPE.FABRIC && (
									<>
										{inventoryInfo(materialStockInfo?.materialStockQtyInfoForKG, true)}
										{inventoryInfo(materialStockInfo?.materialStockQtyInfoForYarn)}
										{inventoryInfo(materialStockInfo?.materialStockQtyInfoForDenim)}
									</>
								)}
							</ul>
						}
					</div>
				)}
				<div className="filter-actions">
					<div className="form-group size32 m-0 search-filter filter-with-animation">
						<div className={'with-icon right-icon'}>
							{requestFrom !== 'FABRIC' && (
								<input
									type="text"
									autoComplete="off"
									name="name"
									ref={inputRef}
									placeholder="Search.."
									defaultValue={commonFilters?.search}
									onChange={() => {
										if (isSearchDone) setIsSearchDone(false);
									}}
									onBlur={(e) => {
										if (!isSearchDone) {
											let searchInputValue = '';
											searchInputValue = e.target.value;
											onSearch(searchInputValue);
										}
									}}
									onKeyDown={(event) => onKeyPressUpdate(event)}
								/>
							)}
							{requestFrom === 'FABRIC' && (
								<FabricSearchWithQR
									setToggleOptions={setToggleOptions}
									toggleOptions={toggleOptions}
									setIsEdit={setIsEdit}
									setSelectedItem={setSelectedItem}
									onSetLikeAndUnlike={onSetLikeAndUnlike}
									setIsInventoryModal={setIsInventoryModal}
									setIsIssueTab={setIsIssueTab}
								/>
							)}

							<span
								className="right-icon d-flex align-items-center"
								onClick={() =>
									requestFrom !== 'FABRIC' && setIsSearchBoxHidden(!isSearchBoxHidden)
								}
							>
								{requestFrom !== 'FABRIC' && (
									<img src={`${process.env.PUBLIC_URL}/icons/search-dark.svg`} />
								)}
							</span>
						</div>
					</div>

					{[MATERIALS_TYPE.FABRIC, MATERIALS_TYPE.TRIMS, MATERIALS_TYPE.ACCESSORIES].includes(
						requestFrom
					) && (
						<>
							{activeTab === 'FABRIC' && (
								<div className="transparent-select country-dropdown mx-12">
									<CustomDropdown
										title={'Select'}
										onItemClick={(selected) => {
											setSelectedBalance({
												id: selected?.id,
												value: selected?.value
											});
										}}
										items={[
											{ id: 'AVAILABLE_BALANCE', value: 'Available Balance' },
											{ id: 'ZERO_BALANCE', value: 'Balance Zero' },
											{ id: 'OTHERS', value: 'Other fabric' }
										]}
										selectedItem={getSelectedBalance()}
									/>
								</div>
							)}
							<div className="transparent-select country-dropdown mx-12">
								<CustomDropdown
									title={'Select'}
									onItemClick={(selected) => {
										setSelectedCountry(selected);
									}}
									items={[{ id: null, value: 'All Country' }].concat(
										getCountryList?.map((item) => ({
											id: item.id,
											value: item.name
										}))
									)}
									selectedItem={selectedCountry?.value || ''}
								/>
							</div>
							<div className="transparent-select sorting-dropdown">
								<CustomDropdown
									title={'Select'}
									onItemClick={(selected) => {
										setSortingValue(selected.id);
										setSelectedSort(selected);
									}}
									items={[
										{ id: 'inventory', value: 'Default sorting' },
										{ id: 'id,desc', value: 'New Arrival' },
										{ id: 'balanceQuantity,desc', value: 'High to Low qty' },
										{ id: 'balanceQuantity', value: 'Low to High qty' },
										{ id: 'lastBookDate', value: 'Recent Booked' },
										{ id: 'lastIssuedDate', value: 'Recent Issued' },
										{ id: 'lastReceivedDate', value: 'Recent Received' }
									]}
									selectedItem={selectedSort?.value || ''}
								/>
							</div>
							<div className="material-list-view-dropdown">
								<ListViewType
									type={VIEW_TYPE_OBJ.MATERIAL_LIST.type}
									onSelectType={(selected) => setDefaultViewType(selected)}
								/>
							</div>
						</>
					)}

					{type === 'SUPPLIER' && (
						<div className="sort-button">
							<select name="sort" id className="border-0" onChange={onSortSupplier}>
								<option value={'id,desc'} selected={commonFilters?.sort === 'id,desc'}>
									Newest first
								</option>
								<option value={'id,asc'} selected={commonFilters?.sort === 'id,asc'}>
									Oldest first
								</option>
							</select>
						</div>
					)}

					{(activeStep === 'FABRIC' || activeStep === 'TRIMS') &&
						checkUserType('PHOTOGRAPHY') &&
						renderBulkUpload()}

					{activeStep !== 'EMBELLISHMENT' &&
						activeStep !== 'GARMENTS' &&
						activeStep !== 'WASHING' &&
						requestFrom !== 'CRM' &&
						!checkUserType('PHOTOGRAPHY') && (
							<div className="add-new-design ml-2">
								<button className="button text size32" onClick={() => onAddClick()}>
									<CommonImage src={`/icons/add-round.svg`} alt="add" />

									<span className="ml-2">{`${title}`}</span>
								</button>
							</div>
						)}
				</div>
			</div>
			<>
				{activeTab !== 'GARMENTS' ? (
					descriptionSearchText ? (
						<ChipForDescriptionSearch />
					) : (
						<Chip />
					)
				) : (
					<div className="chip-container">
						<div className="chips">
							{renderFilteredItems()} {showClearAll()}
						</div>
					</div>
				)}
			</>
		</div>
	);
};

export default TopFilter;
